import {
  create,
  createEvent,
  dispatch,
  previous,
  isEventInLeftHalf,
  isEventInRightHalf,
} from "@hesxenon/prelude/Dom.js";
import * as Either from "@hesxenon/prelude/Either.js";
import { iife, pipe } from "@hesxenon/prelude/Function.js";
import { schedule } from "@hesxenon/prelude/Scheduler.js";
import {
  ariaAttribute__boolean,
  attribute,
  Codec,
  contentAttribute__boolean,
  contentAttribute__number,
  customElement,
} from "simple-custom-elements";

const UwcTableEvents = {
  columnPinned: "uwc-table:columnPinned" as const,
  columnRangeChanged: "uwc-table:columnRangeChanged" as const,
  headerResize: "uwc-table:headerResize" as const,
  rowVisibilityChanged: "uwc-table:row-visiblityChanged" as const,
  columnHidden: "uwc-table:columnHidden" as const,
  moveRequested: "uwc-table:moveRequested" as const,
  moveDenied: "uwc-table:moveDenied" as const,
};
type UwcTableEvents = typeof UwcTableEvents;

/**
 * a 1-based numerical range indicating the beginning and end of a column
 * i.e. how many breakpoints a column spans.
 */
export type ColumnRange = [number, number];
namespace ColumnRange {
  export const equals = (
    [aStart, aEnd]: ColumnRange,
    [bStart, bEnd]: ColumnRange,
  ) => {
    return aStart === bStart && aEnd === bEnd;
  };

  export const getOverlap = (
    [aStart, aEnd]: ColumnRange,
    [bStart, bEnd]: ColumnRange,
  ): ColumnRange | undefined => {
    const start = Math.max(aStart, bStart);
    const end = Math.min(aEnd, bEnd);

    return end <= start ? undefined : [start, end];
  };

  export const getColSpan = ([start, end]: ColumnRange): number => {
    return end - start;
  };

  export const isContained = (
    [aStart, aEnd]: ColumnRange,
    [bStart, bEnd]: ColumnRange,
  ) => {
    return aStart >= bStart && aEnd <= bEnd;
  };

  export const extend = (
    [aStart, aEnd]: ColumnRange,
    [bStart, bEnd]: ColumnRange,
  ) => [Math.min(aStart, bStart), Math.max(aEnd, bEnd)] as ColumnRange;

  export const includes = (index: number, [start, end]: ColumnRange) =>
    index >= start && index <= end;

  /**
   * distance of an index from the edges of a range
   */
  export const getDistance = (index: number, [start, end]: ColumnRange) =>
    index <= start ? index - start : index >= end ? index - end : 0;
}
type Breakpoint = { width: number; explicitlySet: boolean };

/**
 * get the total width of a breakpont range
 */
const getWidth = (
  breakpoints: Breakpoint[],
  containerWidth: number,
  [startCol, endCol]: ColumnRange,
) => {
  return breakpoints
    .slice(startCol - 1, endCol - 1)
    .reduce(
      (sum, { width }, i) =>
        sum +
        (i < breakpoints.length - 1 ? width : Math.abs(containerWidth - sum)),
      0,
    );
};

/**
 * The `<uwc-table>` element gives you the ability to create flexible
 * "data grids" without relying on an abstract JSON-like API. This allows treating a
 * data-grid like any other list - just map over your dataset and create rows, headers
 * and cells as needed.
 */
@customElement({ tagname: "uwc-table" })
export class UwcTableElement extends HTMLElement {
  #disconnect = new AbortController();

  #_breakpoints = [] as Breakpoint[];
  get #breakpoints() {
    return this.#_breakpoints;
  }

  /**
   * the currently set grid breakpoints.
   *
   * @internal
   */
  get breakpoints() {
    return this.#breakpoints;
  }

  set #breakpoints(next) {
    this.#_breakpoints = next;
    this.style.gridTemplateColumns = next
      .map(({ width }) => `${width}px`)
      .join(" ");
  }

  /**
   * @internal
   */
  connectedCallback() {
    this.#disconnect = new AbortController();

    const subtreeObserver = new MutationObserver((records) => {
      const addedRows = records
        .flatMap((record) => Array.from(record.addedNodes))
        .filter((node) => node instanceof UwcTrElement);
      watchRows(addedRows);
    });

    subtreeObserver.observe(this, { childList: true, subtree: true });

    this.#disconnect.signal.addEventListener("abort", () =>
      subtreeObserver.disconnect(),
    );

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          dispatch(entry.target, {
            type: UwcTableEvents.rowVisibilityChanged,
            detail: { isVisible: entry.isIntersecting },
          });
        }
      },
      { root: this, rootMargin: "15%" },
    );

    const watchRows = (rows: Iterable<UwcTrElement>) => {
      for (const row of rows) {
        intersectionObserver.observe(row);
      }
    };

    this.addEventListener(
      UwcTableEvents.headerResize,
      this.#handleBreakpointResize,
    );

    watchRows(this.querySelectorAll("uwc-tr"));

    this.role = "table";
  }

  /**
   * @internal
   */
  disconnectedCallback() {
    this.#disconnect.abort();
  }

  /**
   * move a given header to a specified {@link UwcThElement#order|[order]}.
   *
   * This inserts the header at the specified order, shifting other cells as needed.
   */
  move = ([start, end]: ColumnRange, target: number) => {
    const newStart = Math.max(
      1,
      Math.min(
        target,
        this.breakpoints.length + 1 - (end - start), // make sure new end won't overshoot available breakpoints
      ),
    );
    const moveRequest = createEvent({
      type: UwcTableEvents.moveRequested,
      cancelable: true,
      detail: {
        source: [start, end],
        newStart: newStart,
      },
    });
    dispatch(this, moveRequest);
    if (moveRequest.defaultPrevented) {
      return;
    }
    const nextBreakpoints = this.#breakpoints.slice();
    const breakpointsToMove = nextBreakpoints.splice(start - 1, end - start);
    nextBreakpoints.splice(
      newStart - breakpointsToMove.length,
      0,
      ...breakpointsToMove,
    );
    this.#breakpoints = nextBreakpoints;
  };

  #handleBreakpointResize = iife(() => {
    let defaultColumnSize: number;
    let totalBreakpoints: number;
    return (e: HTMLElementEventMap[UwcTableEvents["headerResize"]]) => {
      const target = e.target as UwcThElement;
      if (this.scrollWidth === 0) {
        requestAnimationFrame(() =>
          dispatch(target, {
            type: UwcTableEvents.headerResize,
            detail: e.detail,
          }),
        );
        return;
      }
      const [startCol, endCol] = target.columnRange;
      const { desiredWidth } = e.detail;

      const currentBreakpoints = this.#breakpoints.slice();

      defaultColumnSize ??=
        this.scrollWidth /
        target.row.cells.reduce(
          (sum, cell) =>
            cell instanceof UwcThElement
              ? cell.hidden
                ? sum
                : sum + cell.colspan
              : sum,
          0,
        );

      totalBreakpoints ??= target.row.cells.reduce(
        (sum, cell) =>
          cell instanceof UwcThElement ? sum + cell.colspan : sum,
        0,
      );

      for (let i = currentBreakpoints.length; i < totalBreakpoints; i++) {
        // just patch the currently known breakpoints so subsequent calculations have the right data
        currentBreakpoints[i] = {
          explicitlySet: false,
          width: Math.max(target.minwidth, defaultColumnSize),
        };
      }

      if (desiredWidth != null) {
        const currentWidth = getWidth(currentBreakpoints, this.scrollWidth, [
          startCol,
          endCol,
        ]);
        const scalingFactor = desiredWidth / currentWidth;
        for (let i = startCol - 1; i < endCol - 1; i++) {
          const breakpoint = currentBreakpoints[i]!;
          breakpoint.explicitlySet = true;
          breakpoint.width =
            desiredWidth === 0 ? 0 : breakpoint.width * scalingFactor;
        }
      } else {
        for (let i = startCol - 1; i < endCol - 1; i++) {
          const breakpoint = currentBreakpoints[i]!;
          breakpoint.explicitlySet = false;
          breakpoint.width = defaultColumnSize;
        }
      }

      this.#breakpoints = currentBreakpoints;
    };
  });
}

/**
 * the `<uwc-tr>` element is used to create rows. These can be nested for row grouping
 */
@customElement({ tagname: "uwc-tr" })
export class UwcTrElement extends HTMLElement {
  /**
   * returns the row that has the same amount of headers as this row
   *
   * undefined means never tried to find it,
   * null means tried to find but came up empty
   */
  get headerRow(): UwcTrElement | undefined {
    const getPrevious = (from: UwcTrElement) =>
      previous("uwc-tr:has(uwc-th)", from, { boundary: this.table });
    let candidate = getPrevious(this);
    while (candidate != null) {
      if (candidate != null && candidate.cells.length === this.cells.length) {
        return candidate;
      }
      candidate = previous("uwc-tr:has(uwc-th)", candidate, {
        boundary: this.table,
      });
    }
  }

  /**
   * whether or not this row is currently visible
   */
  get isVisible() {
    return this.classList.contains("visible");
  }

  /**
   * the cells in this row, ordered by the start of their columnRanges (left to right)
   */
  get cells() {
    return Array.from(
      this.querySelectorAll<UwcThElement | UwcTdElement>(
        ":scope > :where(uwc-td,uwc-th)",
      ),
    ).sort(({ columnRange: a }, { columnRange: b }) =>
      a == null || b == null ? 0 : a[0] - b[0],
    );
  }

  /**
   * the associated table
   */
  get table() {
    const table = this.closest("uwc-table");
    if (table == null) {
      throw new Error(
        "tried to access closest uwc-table but found none! uwc-tr must be nested in uwc-table",
      );
    }
    return table;
  }

  /**
   * the closest uwc-tr, if any.
   *
   * This can be used to retrieve the parent row of a nested row
   */
  get row(): UwcTrElement | undefined {
    return this.parentElement?.closest("uwc-tr") ?? undefined;
  }

  #disconnect!: AbortController;

  /**
   * @internal
   */
  connectedCallback() {
    this.#disconnect = new AbortController();

    this.addEventListener(
      UwcTableEvents.rowVisibilityChanged,
      this.#handleVisibilityChanged,
      this.#disconnect,
    );

    this.role = "row";
  }

  /**
   * @internal
   */
  disconnectedCallback() {
    this.#disconnect.abort();
  }

  /**
   * get a cell by its defined index.
   *
   * This differs from the {@link UwcThElement#order|[order]} as the index will
   * never be changed and has no intrinsic relation to the rendered order
   */
  getCellByIndex(index: number) {
    return this.cells.at(index);
  }

  getCellsInRange = ([start, end]: ColumnRange) => {
    return this.cells.filter((cell) => {
      const [cstart, cend] = cell.columnRange;
      return start <= cstart && cend <= end;
    });
  };

  #handleVisibilityChanged = (
    e: HTMLElementEventMap[UwcTableEvents["rowVisibilityChanged"]],
  ) => {
    if (e.target === this) {
      this.classList.toggle("visible", e.detail.isVisible);
      this.style.setProperty(
        "--level",
        iife(() => {
          let count = 1;
          let cursor = this as UwcTrElement;
          while (cursor.row != null) {
            cursor = cursor.row;
            count++;
          }

          return String(count);
        }),
      );
    }
  };
}

/**
 * a super class to consolidate some behaviour related to all kinds of cells
 */
export class UwcCellElement extends HTMLElement {
  /**
   * get the current range of breakpoints.
   */
  get columnRange() {
    return this.style.gridColumn.split(" / ").map(Number) as ColumnRange;
  }

  /**
   * set the range of breakpoints
   *
   * @hidden
   */
  protected set columnRange(next) {
    const current = this.columnRange;
    this.style.gridColumn = `${next[0]}/${next[1]}`;
    dispatch(this, {
      type: UwcTableEvents.columnRangeChanged,
      detail: { previous: current },
    });
  }

  #table?: UwcTableElement;
  /**
   * return the associated {@link UwcTableElement}
   */
  get table(): UwcTableElement {
    return (this.#table ??= this.closest("uwc-table")!);
  }

  #row?: UwcTrElement;
  /**
   * return the nearest {@link UwcTrElement}
   */
  get row(): UwcTrElement {
    return (this.#row ??= this.closest("uwc-tr")!);
  }

  #index?: number;
  protected get indexOfElement() {
    return (this.#index ??= Array.from(this.row?.children ?? []).indexOf(this));
  }

  protected disconnect!: AbortController;

  /**
   * @internal
   */
  connectedCallback() {
    this.disconnect = new AbortController();
  }

  /**
   * @internal
   */
  disconnectedCallback() {
    this.disconnect.abort();
    this.#index = undefined;
    this.#table = undefined;
    this.#row = undefined;
  }
}

/**
 * the `<uwc-th>` element lets you create column headers.
 *
 * It is worth noting that multiple rows can contain column headers
 * but a single row should never mix headers and cells in its direct children.
 */
@customElement({ tagname: "uwc-th" })
export class UwcThElement extends UwcCellElement {
  /**
   * the breakpoint where this header should be rendered.
   *
   * @remarks Take care when setting this explicitly as it is easy to misalign
   * your grid by accident with this. Multiple headers can have the same order
   * which will cause them to render vertically rather than horizontally.
   *
   * @default the order in which the headers are defined.
   */
  @attribute({
    codec: contentAttribute__number as Codec<string, number>,
    set() {
      this.columnRange = [this.order, this.order + this.colspan];
    },
  })
  order!: number;

  /**
   * how many breakpoints this header should span.
   *
   * Cells that are visually {@link UwcTdElement#columnRange | associated} will copy this
   *
   * @default 1
   */
  @attribute({
    codec: contentAttribute__number as Codec<string, number>,
    set(next) {
      if (this.#next != null) {
        this.#next.order = this.order + next;
      }
    },
  })
  colspan = 1;

  /**
   * whether or not this column is resizeable.
   *
   * @default true
   */
  @attribute({
    codec: contentAttribute__boolean,
    set(next) {
      if (next) {
        this.#addResizeHandle();
      } else {
        this.querySelector("[part='resize-handle']")?.remove();
      }
    },
  })
  resizable = true;

  /**
   * pin a column to the left or right edge. Setting this to `undefined` unpins the column.
   */
  @attribute({
    codec: {
      encode: Either.right,
      decode: (value) =>
        Either.right(
          iife(() => {
            {
              switch (value) {
                case "left":
                  return "left" as const;
                case "right":
                  return "right" as const;
                case undefined:
                default:
                  return undefined;
              }
            }
          }),
        ),
    },
    set(current, previous) {
      if (current === previous) {
        return;
      }
      const move = () => {
        /**
         * columns pinned to the same side
         */
        const otherPinnedColumns = this.row.cells.filter(
          (cell): cell is UwcThElement =>
            cell !== this &&
            cell instanceof UwcThElement &&
            cell.pinned === current,
        );

        const numberOfColumns = this.table.breakpoints.length;

        /**
         * the lowest edge of the currently pinned columns
         */
        const lowerBorder = otherPinnedColumns.reduce(
          (candidate, challenger) => Math.min(candidate, challenger.order),
          numberOfColumns,
        );
        /**
         * the highest edge of the currently pinned columns
         */
        const upperBorder = otherPinnedColumns.reduce(
          (candidate, challenger) =>
            Math.max(candidate, challenger.order + challenger.colspan),
          1, // lowest possible order
        );

        if (current === "left") {
          // if lowest border is not at beginning this should be moved to the beginning
          // can happen during init when multiple columns are pinned in first render
          const targetOrder = lowerBorder === 1 ? upperBorder : 1;
          if (this.order > targetOrder) {
            // is outside of pinned range, move to last pinned position
            this.table.move(this.columnRange, targetOrder);
          }
        } else {
          const targetOrder =
            upperBorder === numberOfColumns ? lowerBorder : numberOfColumns;
          if (this.order < targetOrder) {
            // is outside of pinned range, move to first pinned position
            this.table.move(this.columnRange, targetOrder);
          }
        }
        dispatch(this, { type: UwcTableEvents.columnPinned });
      };

      if (this.order == null) {
        this.addEventListener(
          UwcTableEvents.columnRangeChanged,
          () => {
            // initial columnRangeChanged triggers setup of column template, give it some time to complete
            schedule(move);
          },
          { once: true },
        );
      } else {
        move();
      }
    },
  })
  pinned?: "left" | "right";

  /**
   * define an explicit width. This always returns the width according
   * to the breakpoints that are currently set on its {@link UwcThElement#table|`<uwc-table>`}
   *
   * @remarks this is also set when the user resizes the header.
   *
   */
  @attribute({
    codec: contentAttribute__number as Codec<string, number>,
    get() {
      return getWidth(
        this.table.breakpoints,
        this.table.scrollWidth,
        this.columnRange,
      );
    },
    set(next) {
      const dispatchResize = () => {
        dispatch(this, {
          type: UwcTableEvents.headerResize,
          detail: { desiredWidth: this.hidden ? 0 : next },
        });
      };

      if (this.order == null) {
        this.addEventListener(
          UwcTableEvents.columnRangeChanged,
          () => {
            // initial columnRangeChanged triggers setup of column template, give it some time to complete
            schedule(dispatchResize);
          },
          {
            once: true,
          },
        );
      } else {
        dispatchResize();
      }
    },
  })
  width!: number;

  /**
   * define a minimum width in pixel for the column
   *
   * @default 20
   */
  @attribute({ codec: contentAttribute__number as Codec<string, number> })
  minwidth = 20;

  /**
   * whether or not this column can be re-ordered via drag-and-drop.
   *
   * This only has an effect on column headers in the very first row of a table.
   *
   * @default true
   */
  @attribute({ codec: ariaAttribute__boolean as Codec<string, boolean> })
  draggable = true;

  /**
   * whether or not this header and its {@link UwcTdElement#columnHeader | associated} cells
   * should be hidden from rendering.
   *
   * @default false
   */
  @attribute({
    codec: contentAttribute__boolean,
    set() {
      const dispatchEvents = () => {
        const width = pipe(
          contentAttribute__number.decode(this.getAttribute("width")),
          Either.getOrElse(() => undefined as undefined | number),
        );

        dispatch(this, { type: UwcTableEvents.columnHidden });
        dispatch(this, {
          type: UwcTableEvents.headerResize,
          detail: { desiredWidth: this.hidden ? 0 : width },
        });
      };

      if (this.order == null) {
        this.addEventListener(
          UwcTableEvents.columnRangeChanged,
          dispatchEvents,
          {
            once: true,
          },
        );
      } else {
        dispatchEvents();
      }
    },
  })
  hidden = false;

  static #draggedElement?: UwcThElement;

  /**
   * get the associated {@link UwcThElement | `<uwc-th>`} if any.
   *
   * Associated means the {@link UwcThElement | `<uwc-th>`} that is in a
   * previous {@link UwcTrElement | `<uwc-tr>`} which completely covers
   * this headers {@link UwcThElement#columnRange | `columnRange`}
   */
  get columnHeader() {
    return previous("uwc-tr:has(uwc-th)", this.row, {
      boundary: this.table,
    })?.cells.find(
      (cell): cell is UwcThElement =>
        cell instanceof UwcThElement &&
        cell.columnRange[0] <= this.columnRange[0] &&
        cell.columnRange[1] >= this.columnRange[1],
    );
  }

  get #allPrevious() {
    return this.row.cells.filter(
      (other): other is UwcThElement =>
        other instanceof UwcThElement &&
        other.order < (this.order ?? Number.POSITIVE_INFINITY),
    );
  }

  get #previous() {
    return this.#allPrevious.reduce(
      (candidate, challenger): typeof candidate =>
        candidate != null && candidate.order >= challenger.order
          ? candidate
          : challenger,
      undefined as undefined | UwcThElement,
    );
  }

  get #allNext() {
    return this.row.cells.filter(
      (other): other is UwcThElement =>
        other instanceof UwcThElement &&
        other.order > (this.order ?? Number.NEGATIVE_INFINITY),
    );
  }

  get #next() {
    return this.#allNext.reduce(
      (candidate, challenger): typeof candidate =>
        candidate != null && candidate.order <= challenger.order
          ? candidate
          : challenger,
      undefined as undefined | UwcThElement,
    );
  }

  /**
   * @internal
   */
  connectedCallback() {
    super.connectedCallback();

    this.table.addEventListener(
      UwcTableEvents.moveRequested,
      /**
       * check whether or not the current move would break
       * a header out of my group. If so, prevent the event.
       *
       * Doing this in the capture phase allows us to prevent
       * the event from ever reaching its target.
       */
      (e) => {
        const {
          source: [start, end],
          newStart,
        } = e.detail;
        const myRange = this.columnRange;
        if (
          !ColumnRange.equals(e.detail.source, myRange) &&
          ColumnRange.isContained(e.detail.source, myRange) &&
          !ColumnRange.isContained(
            [newStart, newStart + (end - start)],
            myRange,
          )
        ) {
          // a column header is dragged that overlaps with me, but to a place that isn't contained in me -> the implied grouping would be broken
          e.preventDefault();
          e.stopPropagation();
          dispatch(this, {
            type: UwcTableEvents.moveDenied,
            detail: { reason: "would break up group" },
          });
        }
      },
      { capture: true, signal: this.disconnect.signal },
    );

    this.table.addEventListener(
      UwcTableEvents.moveRequested,
      /**
       * react to the move request in bubble phase
       */
      ({
        detail: {
          source: [start, end],
          newStart,
        },
      }) => {
        const affectedRange = [
          Math.min(start, newStart),
          Math.min(
            this.table.breakpoints.length + 1,
            Math.max(end, newStart + (end - start)),
          ),
        ] as ColumnRange;
        const distanceToMove = newStart - start;
        if (
          distanceToMove === 0 ||
          ColumnRange.getOverlap(this.columnRange, affectedRange) == null ||
          ColumnRange.isContained(affectedRange, this.columnRange)
        ) {
          // not affected by the move
          return;
        }
        if (ColumnRange.getOverlap(this.columnRange, [start, end]) != null) {
          this.order += distanceToMove;
        } else {
          this.order -= newStart > start ? end - start : start - end;
        }
      },
      { signal: this.disconnect.signal },
    );

    this.addEventListener("dragstart", () => {
      this.classList.add("dragging");
      UwcThElement.#draggedElement = this;
    });

    this.addEventListener("dragend", () => {
      this.classList.remove("dragging");
      UwcThElement.#draggedElement = undefined;
    });

    this.addEventListener("dragover", (e) => {
      const draggedElement = UwcThElement.#draggedElement;
      if (draggedElement == null || this === draggedElement) {
        return;
      }
      e.preventDefault();
      const [myStart] = this.columnRange;
      const [draggedStart, draggedEnd] = draggedElement.columnRange;
      const isBefore = draggedStart < myStart;
      const shouldInsertBefore = isEventInLeftHalf(e, this);
      const isAfter = draggedStart > myStart;
      const shouldInsertAfter = isEventInRightHalf(e, this);
      const newStart =
        isBefore && shouldInsertAfter
          ? myStart - (draggedElement.colspan - this.colspan) // account for size difference
          : isAfter && shouldInsertBefore
            ? myStart
            : undefined;

      if (newStart != null) {
        this.table.move([draggedStart, draggedEnd], newStart);
      }
    });

    this.addEventListener(
      UwcTableEvents.columnRangeChanged,
      this.#setupPinnedStyling,
    );

    this.table.addEventListener(
      UwcTableEvents.headerResize,
      this.#setupPinnedStyling,
      this.disconnect,
    );

    schedule(() => {
      this.order ??= this.#previous?.columnRange[1] ?? 1;
    });

    this.role = "columnheader";
  }

  #setupPinnedStyling = () => {
    switch (this.pinned) {
      case undefined:
        return;
      case "left":
        this.style.right = "";
        this.style.left = `${this.table.breakpoints.slice(0, this.order - 1).reduce((sum, { width }) => sum + width, 0)}px`;
        this.classList.toggle(
          "last",
          this.row.cells.reduce(
            (highestPinned, cell) =>
              cell instanceof UwcThElement &&
              cell.pinned === this.pinned &&
              cell.order > highestPinned
                ? cell.order
                : highestPinned,
            1,
          ) === this.order,
        );
        break;
      case "right":
        this.style.left = "";
        this.style.right = `${this.table.breakpoints.slice(this.order).reduce((sum, { width }) => sum + width, 0)}px`;
        this.classList.toggle(
          "first",
          this.row.cells.reduce(
            (lowestPinned, cell) =>
              cell instanceof UwcThElement &&
              cell.pinned === this.pinned &&
              cell.order < lowestPinned
                ? cell.order
                : lowestPinned,
            this.table.breakpoints.length,
          ) === this.order,
        );
        break;
    }
  };

  #addResizeHandle = () => {
    if (this.querySelector(".resize-handle") != null) {
      // TODO: remove this check as soon as default value setting in decorator is fixed
      return;
    }
    const resizeHandle = create("div", { className: "resize-handle" });
    this.append(resizeHandle);
    let lastX: number;
    let width: number;
    let stopResize = new AbortController();
    const drag = (e: MouseEvent) => {
      width = Math.max(this.minwidth, width + (e.clientX - lastX));
      lastX = e.clientX;
      requestAnimationFrame(() => {
        this.width = width;
      });
    };

    resizeHandle.addEventListener(
      "mousedown",
      (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        lastX = e.clientX;
        width = this.width;
        stopResize = new AbortController();
        document.addEventListener("mousemove", drag, stopResize);
        document.addEventListener(
          "mouseup",
          () => {
            stopResize.abort();
          },
          stopResize,
        );
      },
      this.disconnect,
    );
  };
}

/**
 * The `<uwc-td>` element lets you create cells for display data points
 */
@customElement({ tagname: "uwc-td" })
export class UwcTdElement extends UwcCellElement {
  /**
   * return the {@link UwcThElement | `<uwc-th>`} this cell is associated to if any.
   *
   * "Associated" means the header at _the same index_ (not order) as this cell in the
   * closest previous row that has _the same amount of uwc-th_ elements as this cells
   * row
   */
  get columnHeader() {
    return this.row.headerRow?.children.item(this.indexOfElement) as
      | UwcThElement
      | undefined;
  }

  /**
   * how many breakpoints this cell spans.
   *
   * This is copied from its {@link UwcTdElement#columnHeader | associated header}
   */
  get colspan() {
    return this.columnRange[1] - this.columnRange[0];
  }

  /**
   * whether or not this cell is currently visible.
   *
   * At the moment this is true as long as its containing {@link UwcTrElement | `<uwc-tr>`}
   * is visible
   */
  get isVisible() {
    return this.row.isVisible;
  }

  #visibility = new AbortController();

  /**
   * @internal
   */
  connectedCallback() {
    super.connectedCallback();

    this.row.addEventListener(
      UwcTableEvents.rowVisibilityChanged,
      this.#handleVisibilityChanged,
      this.disconnect,
    );

    this.role = "cell";
  }

  #handleVisibilityChanged = () => {
    if (!this.isVisible) {
      this.#visibility.abort();
      return;
    }
    this.#visibility = new AbortController();

    this.#attachToColumnHeader();
  };

  #attachToColumnHeader = () => {
    const header = this.columnHeader;
    if (header == null || !(header instanceof UwcThElement)) {
      return;
    }
    const copyHidden = () => {
      if (header.hidden) {
        this.setAttribute("hidden", "");
      } else {
        this.removeAttribute("hidden");
      }
    };
    const copyColumnRange = () => {
      this.columnRange = header.columnRange;
    };
    const copyPinned = () => {
      if (header.pinned != null) {
        this.setAttribute("pinned", header.pinned);
      } else {
        this.removeAttribute("pinned");
      }
      this.style.left = header.style.left;
      this.style.right = header.style.right;
      this.className = header.className;
    };
    copyColumnRange();
    copyHidden();
    copyPinned();
    header.addEventListener(
      UwcTableEvents.columnRangeChanged,
      copyColumnRange,
      this.#visibility,
    );
    header.addEventListener(
      UwcTableEvents.columnPinned,
      copyPinned,
      this.#visibility,
    );
    this.table.addEventListener(
      UwcTableEvents.headerResize,
      copyPinned,
      this.#visibility,
    );
    header.addEventListener(
      UwcTableEvents.columnHidden,
      copyHidden,
      this.#visibility,
    );
  };
}

declare global {
  const UwcTableElement: typeof import("./uwc-table.ts").UwcTableElement;
  type UwcTableElement = InstanceType<typeof UwcTableElement>;

  const UwcTrElement: typeof import("./uwc-table.ts").UwcTrElement;
  type UwcTrElement = InstanceType<typeof UwcTrElement>;

  const UwcThElement: typeof import("./uwc-table.ts").UwcThElement;
  type UwcThElement = InstanceType<typeof UwcThElement>;

  const UwcTdElement: typeof import("./uwc-table.ts").UwcTdElement;
  type UwcTdElement = InstanceType<typeof UwcTdElement>;

  interface HTMLElementEventMap {
    [UwcTableEvents.columnRangeChanged]: CustomEvent<{ previous: ColumnRange }>;
    [UwcTableEvents.headerResize]: CustomEvent<{
      desiredWidth: number | undefined;
    }>;
    [UwcTableEvents.rowVisibilityChanged]: CustomEvent<{ isVisible: boolean }>;
    [UwcTableEvents.moveRequested]: CustomEvent<{
      source: ColumnRange;
      newStart: number;
    }>;
    [UwcTableEvents.moveDenied]: CustomEvent<{ reason: string }>;
  }

  interface HTMLElementTagNameMap {
    "uwc-table": UwcTableElement;
    "uwc-tr": UwcTrElement;
    "uwc-th": UwcThElement;
    "uwc-td": UwcTdElement;
  }
}
Object.assign(globalThis, {
  UwcTableElement,
  UwcTdElement,
  UwcTrElement,
  UwcThElement,
});
